import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, X, Shield, TrendingUp, Zap, Building2, Users, BarChart3, Award, Lock, Globe, Target, Database, Sparkles, ChevronDown, ShieldCheck, Percent } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PublicNav from '../../components/publicNav/PublicNav';
import PricingWaitlistForm from './PricingWaitlistForm';
import { InvestmentDisclaimerNotice } from '../../components/Compliance/ComplianceNotices';
import { fetchMonetizationConfigThunk } from '../../redux/monetization/monetizationActionThunks';
import { selectMonetizationConfig } from '../../redux/monetization/monetizationSlice';
import { GUARDRAIL_ORDER, GUARDRAIL_INFO, formatPriceCeiling, translateGuardrailDescription } from '../../utils/groupGuardrailTiers';
import { tierColors } from '../../utils/credibilityTiers';

// Real credibility tier -> guardrail state, mirrors
// monetization_guardrails.py's GUARDRAIL_STATES exactly (display purposes
// only here -- every real enforcement decision is server-side, same
// disclaimer groupGuardrailTiers.js itself carries).
const CREDIBILITY_TIER_FOR_STATE = { sandbox: 'Beginner', guarded: 'Advanced', extended: 'Elite', unrestricted: 'Top Emperor' };

// Mirrors take_rates.py's TIER_TAKE_RATES -- kept as a named, commented
// constant (not fetched) since this is a static, rarely-changing product
// fact and this page has no other reason to round-trip the server before
// first paint. A drift here would only ever understate/overstate the
// marketing copy, never affect what a creator is actually charged --
// that's computed server-side at checkout time regardless of this page.
const TAKE_RATE_FOR_TIER = { Advanced: 0.25, Elite: 0.20, 'Top Emperor': 0.15 };

// ---------------------------------------------------------------------------
// Legacy content below (BUNDLE_META / COMPARISON_META / API_ENDPOINT_NAMES
// and the sections that render them) is the old Stocker-as-a-SaaS pricing
// model -- per-ticker-unlock Free/Pro/Premium/Enterprise tiers compared
// against TradingView/Seeking Alpha/Koyfin. That was never the real,
// implemented business model (no backend anywhere gates ticker depth by
// plan) and isn't the one currently being built (group monetization --
// see monetization_guardrails.py/take_rates.py). Retired behind
// `legacyPricingTiers` (default off, admin-toggleable, see migration
// 7b2c4f9a1e6d) rather than deleted, in case a future business decision
// revives this shape.
// ---------------------------------------------------------------------------
const BUNDLE_META = [
  { price: { monthly: 0, annual: 0 }, highlight: false, icon: Users },
  { price: { monthly: 9.99, annual: 99 }, highlight: true, icon: TrendingUp },
  { price: { monthly: 19.99, annual: 199 }, highlight: false, icon: BarChart3 },
  { price: { monthly: 'Custom', annual: 'Custom' }, highlight: false, icon: Building2 },
];

const COMPARISON_META = [
  { name: 'TradingView', price: 24.95 },
  { name: 'Seeking Alpha', price: 29.99 },
  { name: 'Koyfin', price: 29 },
  { name: 'Stocker (Per-Month)', price: 8.25, highlight: true },
];

const API_ENDPOINT_NAMES = [
  '/ticker/{symbol}/sentiment',
  '/ticker/{symbol}/credibility',
  '/ticker/{symbol}/context',
  '/ticker/{symbol}/export',
];

const PricingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [billingCycle, setBillingCycle] = useState('annual');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const legacyPricingTiers = useSelector((s) => s.featureFlags.flags.legacyPricingTiers);
  const monetizationConfig = useSelector(selectMonetizationConfig);

  useEffect(() => { dispatch(fetchMonetizationConfigThunk()); }, [dispatch]);

  const bundles = legacyPricingTiers
    ? t('pricing.bundles', { returnObjects: true }).map((b, i) => ({ ...b, ...BUNDLE_META[i] }))
    : [];
  const comparisonPlatforms = legacyPricingTiers
    ? t('pricing.comparison.platforms', { returnObjects: true }).map((p, i) => ({ ...p, ...COMPARISON_META[i] }))
    : [];
  const apiEndpoints = legacyPricingTiers
    ? t('pricing.api.endpoints', { returnObjects: true }).map((e, i) => ({ ...e, name: API_ENDPOINT_NAMES[i] }))
    : [];

  const faqs = t('pricing.monetizationFaq.items', { returnObjects: true });

  return (
    <div
      className="w-full min-h-screen relative overflow-hidden bg-white dark:bg-[#09090f]"
      style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
    >
      <Helmet>
        <title>Pricing | Stocker Beta</title>
        <meta
          name="description"
          content="Stocker's monetization tiers scale with verified credibility, not a flat subscription -- see what each tier unlocks for creators and groups."
        />
        <link rel="canonical" href="https://stocker-beta.com/pricing" />
      </Helmet>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');`}</style>
      <div
        style={{
          position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
          width: 900, height: 480,
          background: 'radial-gradient(ellipse at 50% -10%, rgba(109,40,217,0.16) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />

    {/* Navigation (shared across every public page) */}
    <PublicNav />

      {/* Hero Section -- group monetization, the real live mechanism */}
      <section className="max-w-7xl mx-auto px-4 py-16 text-center">
        <div className="inline-flex items-center gap-2 bg-violet-50 dark:bg-violet-500/20 px-4 py-2 rounded-full border border-violet-300 dark:border-violet-500/30 mb-6">
          <Sparkles className="text-violet-600 dark:text-violet-400" size={16} />
          <span className="text-sm text-violet-700 dark:text-violet-300">{t('pricing.monetization.hero.badge')}</span>
        </div>
        <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
          {t('pricing.monetization.hero.titleLine1')}<br />{t('pricing.monetization.hero.titleLine2')}
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          {t('pricing.monetization.hero.subtitle')}
        </p>
      </section>

      {/* How it works -- 3 real steps, explicitly correcting the "no group
          creation below Advanced" misconception: creating a group is free
          at every tier, only charging for one requires Advanced. */}
      <section className="max-w-7xl mx-auto px-4 pb-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Users, key: 'create' },
            { icon: ShieldCheck, key: 'build' },
            { icon: Percent, key: 'unlock' },
          ].map(({ icon: Icon, key }, i) => (
            <div key={key} className="bg-cream-900/[0.03] dark:bg-white/5 backdrop-blur-sm border border-violet-300 dark:border-violet-500/20 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center text-violet-700 dark:text-violet-300 font-bold text-sm flex-shrink-0">
                  {i + 1}
                </div>
                <Icon className="text-violet-600 dark:text-violet-400" size={20} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{t(`pricing.monetization.steps.${key}.title`)}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{t(`pricing.monetization.steps.${key}.desc`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Real guardrail tier ladder -- same data source, same numbers, as
          the actual in-app system (groupGuardrailTiers.js / GroupPricing.jsx
          / GuardrailTierPanel.jsx). Every tier's description is shown
          inline (not hidden behind a hover-only reveal, which doesn't work
          on touch) so a tier is never presented as a bare, unqualified
          badge next to a price. */}
      <section className="max-w-7xl mx-auto px-4 pb-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">{t('pricing.monetization.tiers.title')}</h2>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">{t('pricing.monetization.tiers.subtitle')}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {GUARDRAIL_ORDER.map((state) => {
            const credTier = CREDIBILITY_TIER_FOR_STATE[state];
            const info = GUARDRAIL_INFO[state];
            const colors = tierColors(credTier);
            const takeRate = TAKE_RATE_FOR_TIER[credTier];
            return (
              <div key={state} className={`rounded-2xl p-6 border bg-cream-900/[0.03] dark:bg-white/5 backdrop-blur-sm ${colors.border}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold ${colors.bg} ${colors.text} border ${colors.border}`}>
                    {credTier}
                  </span>
                  {info.allowsPublicGroup ? (
                    <span className="flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400"><Globe size={12} /> {t('pricing.monetization.tiers.publicEligible')}</span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-gray-500"><Lock size={12} /> {t('pricing.monetization.tiers.privateOnly')}</span>
                  )}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
                  {translateGuardrailDescription(state, t)}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-white/10">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{t('pricing.monetization.tiers.priceCeiling')}</div>
                    <div className="text-lg font-bold text-gray-900 dark:text-white">{formatPriceCeiling(info.priceCeilingCents, t)}</div>
                  </div>
                  {takeRate != null && (
                    <div className="text-right">
                      <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{t('pricing.monetization.tiers.platformTakeRate')}</div>
                      <div className="text-lg font-bold text-gray-900 dark:text-white">{Math.round(takeRate * 100)}%</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-500 mt-6 max-w-2xl mx-auto text-center">
          {t('pricing.monetization.tiers.footnote')}
        </p>
      </section>

      {/* Disclaimer -- the exact "paying for a credible person's opinions"
          surface, same context used inside a real group's own Pricing tab. */}
      <section className="max-w-3xl mx-auto px-4 pb-16">
        <InvestmentDisclaimerNotice context="subscription" />
      </section>

      {/* CTA -- gated on the REAL, live MONETIZATION_LIVE kill-switch
          (fetched from GET /monetization/config, public, no auth needed),
          not a separate frontend-only flag that could drift out of sync
          with the backend's actual state. */}
      <section className="max-w-3xl mx-auto px-4 pb-24 text-center">
        {monetizationConfig.monetization_live ? (
          <>
            <p className="text-gray-600 dark:text-gray-300 mb-5">{t('pricing.monetization.cta.liveSubtitle')}</p>
            <button
              onClick={() => navigate('/groupies')}
              className="bg-violet-600 hover:bg-violet-500 text-white px-8 py-3.5 rounded-lg font-semibold transition-all shadow-lg"
            >
              {t('pricing.monetization.cta.liveButton')}
            </button>
          </>
        ) : (
          <>
            <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/30 rounded-xl mb-6 text-left">
              <Zap size={18} className="text-amber-700 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">{t('pricing.monetization.cta.testModeTitle')}</p>
                <p className="text-xs text-amber-700 dark:text-amber-400/80 mt-1">{t('pricing.monetization.cta.testModeBody')}</p>
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-5">{t('pricing.monetization.cta.waitlistSubtitle')}</p>
            <PricingWaitlistForm className="max-w-md mx-auto" />
          </>
        )}
      </section>

      {legacyPricingTiers && (
        <>
          {/* Billing Toggle (legacy) */}
          <section className="max-w-7xl mx-auto px-4 text-center pb-6">
            <div className="flex items-center justify-center gap-4 mb-16">
              <span className={`text-sm ${billingCycle === 'monthly' ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                {t('pricing.hero.monthly')}
              </span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                role="switch"
                aria-checked={billingCycle === 'annual'}
                aria-label={t('pricing.hero.toggleAriaLabel')}
                className="relative w-14 h-8 bg-violet-600 rounded-full transition-colors"
              >
                <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${billingCycle === 'annual' ? 'translate-x-6' : ''}`} />
              </button>
              <span className={`text-sm ${billingCycle === 'annual' ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                {t('pricing.hero.annual')}
              </span>
            </div>
          </section>

          {/* Pricing Cards (legacy) */}
          <section className="max-w-7xl mx-auto px-4 pb-20">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {bundles.map((tier, index) => (
                <div
                  key={index}
                  className={`relative rounded-2xl p-8 ${
                    tier.highlight
                      ? 'bg-gradient-to-br from-violet-600 to-indigo-700 border-2 border-violet-400'
                      : 'bg-cream-900/[0.03] dark:bg-white/5 backdrop-blur-sm border border-violet-300 dark:border-violet-500/20'
                  } hover:border-violet-500/40 transition-all`}
                >
                  {tier.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-gray-900 px-4 py-1 rounded-full text-xs font-semibold">
                      {tier.badge}
                    </div>
                  )}

                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-2 rounded-lg ${tier.highlight ? 'bg-white/20' : 'bg-violet-100 dark:bg-violet-500/20'}`}>
                      <tier.icon className={tier.highlight ? 'text-white' : 'text-violet-600 dark:text-violet-400'} size={24} />
                    </div>
                    <h3 className={`text-2xl font-bold ${tier.highlight ? 'text-white' : 'text-gray-900 dark:text-white'}`}>{tier.name}</h3>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-2">
                      {typeof tier.price[billingCycle] === 'number' ? (
                        <>
                          <span className={`text-4xl font-bold ${tier.highlight ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                            €{billingCycle === 'annual' ? tier.price.annual : tier.price.monthly}
                          </span>
                          <span className={tier.highlight ? 'text-gray-300' : 'text-gray-600 dark:text-gray-300'}>
                            /{billingCycle === 'annual' ? t('pricing.perYearSuffix') : t('pricing.perMonthSuffix')}
                          </span>
                        </>
                      ) : (
                        <span className={`text-4xl font-bold ${tier.highlight ? 'text-white' : 'text-gray-900 dark:text-white'}`}>{tier.price[billingCycle]}</span>
                      )}
                    </div>
                    {billingCycle === 'annual' && typeof tier.price.annual === 'number' && tier.price.annual > 0 && (
                      <p className={`text-sm mt-2 ${tier.highlight ? 'text-gray-300' : 'text-gray-600 dark:text-gray-300'}`}>
                        {t('pricing.billedAnnually', { amount: (tier.price.annual / 12).toFixed(2) })}
                      </p>
                    )}
                  </div>

                  <p className={`mb-6 text-sm ${tier.highlight ? 'text-gray-300' : 'text-gray-600 dark:text-gray-300'}`}>{tier.description}</p>

                  <button onClick={() => navigate('/')} className={`w-full py-3 rounded-lg font-semibold transition-all mb-6 ${
                      tier.highlight
                        ? 'bg-white hover:bg-gray-100'
                        : 'bg-violet-600 text-white hover:bg-violet-500'
                    }`}
                    style={tier.highlight ? { color: '#4c1d95' } : undefined}
                  >
                    {tier.cta}
                  </button>

                  <div className="space-y-3">
                    {tier.features.map((feature, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className={`${tier.highlight ? 'text-white' : 'text-violet-600 dark:text-violet-400'} flex-shrink-0 mt-0.5`} size={18} />
                        <span className={`text-sm ${tier.highlight ? 'text-gray-300' : 'text-gray-600 dark:text-gray-300'}`}>{feature}</span>
                      </div>
                    ))}
                    {tier.limitations.map((limitation, i) => (
                      <div key={i} className="flex items-start gap-2 opacity-60">
                        <X className="text-gray-500 flex-shrink-0 mt-0.5" size={18} />
                        <span className={`text-sm ${tier.highlight ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}>{limitation}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Comparison Section (legacy) */}
          <section className="bg-cream-900/[0.04] dark:bg-black/30 backdrop-blur-sm py-20 border-y border-violet-300 dark:border-violet-500/20">
            <div className="max-w-7xl mx-auto px-4">
              <h2 className="text-4xl font-bold text-center text-gray-900 dark:text-white mb-4">{t('pricing.comparison.title')}</h2>
              <p className="text-center text-gray-600 dark:text-gray-300 mb-12 max-w-2xl mx-auto">
                {t('pricing.comparison.subtitle')}
              </p>

              <div className="max-w-3xl mx-auto">
                {comparisonPlatforms.map((platform, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-6 rounded-lg mb-4 ${
                      platform.highlight
                        ? 'bg-violet-100 dark:bg-violet-600/20 border-2 border-violet-400'
                        : 'bg-cream-900/[0.03] dark:bg-white/5 backdrop-blur-sm border border-violet-300 dark:border-violet-500/20'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{platform.name}</h3>
                        {platform.highlight && (
                          <span className="bg-yellow-500 text-gray-900 px-2 py-1 rounded text-xs font-semibold">
                            {t('pricing.comparison.bestValue')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{platform.value}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">€{platform.price}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{t('pricing.comparison.perMonthLabel')}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* API Endpoints Section (legacy) */}
          <section className="py-20 max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">{t('pricing.api.title')}</h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                {t('pricing.api.subtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {apiEndpoints.map((endpoint, index) => (
                <div key={index} className="bg-cream-900/[0.03] dark:bg-white/5 backdrop-blur-sm border border-violet-300 dark:border-violet-500/20 rounded-lg p-6 hover:border-violet-500/40 transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <code className="text-violet-700 dark:text-violet-400 font-mono text-sm bg-violet-100 dark:bg-violet-500/10 px-3 py-1 rounded">
                      {endpoint.name}
                    </code>
                    <span className="text-xs bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300 px-2 py-1 rounded border border-violet-300 dark:border-violet-500/30">
                      {endpoint.tier}
                    </span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">{endpoint.description}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <button
                onClick={() => navigate('/contact?type=sales')}
                className="bg-violet-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-violet-500 transition-all shadow-lg inline-flex items-center gap-2"
              >
                <Database size={20} />
                {t('pricing.api.requestDocs')}
              </button>
            </div>
          </section>
        </>
      )}

      {/* Trust Badges -- platform-level claims, true regardless of pricing model */}
      <section className="relative z-10 py-16" style={{ background: 'linear-gradient(135deg, #4c1d95 0%, #4338ca 50%, #1e3a8a 100%)' }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <Shield className="text-violet-300 mx-auto mb-3" size={32} />
              <h3 className="text-white font-semibold mb-2">{t('pricing.trust.euCompliant.title')}</h3>
              <p className="text-sm text-gray-300">{t('pricing.trust.euCompliant.desc')}</p>
            </div>
            <div>
              <Lock className="text-violet-300 mx-auto mb-3" size={32} />
              <h3 className="text-white font-semibold mb-2">{t('pricing.trust.bankGrade.title')}</h3>
              <p className="text-sm text-gray-300">{t('pricing.trust.bankGrade.desc')}</p>
            </div>
            <div>
              <Target className="text-violet-300 mx-auto mb-3" size={32} />
              <h3 className="text-white font-semibold mb-2">{t('pricing.trust.explainableAI.title')}</h3>
              <p className="text-sm text-gray-300">{t('pricing.trust.explainableAI.desc')}</p>
            </div>
            <div>
              <Award className="text-violet-300 mx-auto mb-3" size={32} />
              <h3 className="text-white font-semibold mb-2">{t('pricing.trust.credibilityFirst.title')}</h3>
              <p className="text-sm text-gray-300">{t('pricing.trust.credibilityFirst.desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ -- rewritten for the real group-monetization mechanism */}
      <section className="py-20 max-w-4xl mx-auto px-4">
        <h2 className="text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">{t('pricing.monetizationFaq.title')}</h2>
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-cream-900/[0.03] dark:bg-white/5 backdrop-blur-sm border border-violet-300 dark:border-violet-500/20 rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                className="w-full p-6 text-left flex items-center justify-between hover:bg-cream-900/[0.03] dark:hover:bg-white/5 transition-colors"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white pr-4">{faq.q}</h3>
                <ChevronDown
                  className={`text-violet-600 dark:text-violet-400 flex-shrink-0 transition-transform ${expandedFaq === i ? 'rotate-180' : ''}`}
                  size={20}
                />
              </button>
              {expandedFaq === i && (
                <div className="px-6 pb-6">
                  <p className="text-gray-600 dark:text-gray-300">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 py-20" style={{ background: 'linear-gradient(135deg, #4c1d95 0%, #4338ca 50%, #1e3a8a 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">{t('pricing.monetization.finalCta.title')}</h2>
          <p className="text-xl text-gray-200 mb-10">
            {t('pricing.monetization.finalCta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="bg-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all shadow-lg"
              style={{ color: '#4c1d95' }}
            >
              {t('pricing.monetization.finalCta.startFree')}
            </button>
            <button
              onClick={() => navigate('/contact?type=sales')}
              className="bg-white/10 backdrop-blur-md text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/20 transition-all border border-white/30"
            >
              {t('pricing.monetization.finalCta.contactSales')}
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 backdrop-blur-xl border-t border-white/10 py-8" style={{ background: 'rgba(9,9,15,0.7)' }}>
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-400">
            {t('pricing.footer')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
